import { inject, Injectable } from '@angular/core';
import { ILink, IProductListLink } from '@epicuro-next/data/merchandise';
import { BasketItemSource } from '@epicuro-next/models/basket';
import {
  getListIdFromProductListUrl,
  isListUrl,
  isLookbookUrl,
  isSearchUrl,
} from '@epicuro-next/navigation/routes';
import {
  EcommercePromotionSource,
  EcommerceTrackingEvents,
} from '@epicuro-next/platform/tracking';
import { setProductSource } from '@epicuro-next/state/config-strip-state';
import { trackList } from '@epicuro-next/state/product-list-state';
import { gtmTrackActions } from '@epicuro-next/state/tracking';
import { urlToLinkInfo } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';

@Injectable()
export class LandingPageComponentStore extends ComponentStore<{}> {
  private readonly store = inject(Store);
  constructor() {
    super({});
  }

  public trackRedirection(
    href: string,
    creative: EcommercePromotionSource,
    position: number,
    link?: ILink,
  ) {
    this.trackPromotion(href, creative, position);
    this.activateSource(href, link);
  }

  private activateSource(href: string, link?: ILink) {
    if (
      isListUrl(href) ||
      (link as IProductListLink)?.linkParams?.productList
    ) {
      const productListId =
        (link as IProductListLink)?.linkParams?.productList ??
        getListIdFromProductListUrl(href);
      this.store.dispatch(
        setProductSource({
          source: BasketItemSource.LIST_LANDING_PAGE,
          details: {
            listId: [productListId],
          },
        }),
      );
      this.store.dispatch(
        trackList({
          payload: {
            productListId,
            source: 'landing-page',
          },
        }),
      );
      return;
    }

    if (isSearchUrl(href)) {
      const { params } = urlToLinkInfo(href);

      const details = {
        query: params?.q ?? '',
        searchParams: params ?? {},
      };
      this.store.dispatch(
        setProductSource({
          source: BasketItemSource.SEARCH_LANDING_PAGE,
          details: {
            ...details,
            linkUrl: href,
          },
          additionalUiDetails: details,
        }),
      );
      return;
    }

    if (isLookbookUrl(href)) {
      const { url } = urlToLinkInfo(href);

      const [listId = '', lookbookId = ''] = url.split('/').reverse();
      this.store.dispatch(
        setProductSource({
          source: BasketItemSource.LIST_LANDING_PAGE,
          details: {
            listId: [listId],
            lookBookId: [lookbookId],
          },
        }),
      );
      this.store.dispatch(
        trackList({
          payload: {
            productListId: listId,
            source: 'landing-page',
          },
        }),
      );
    }
  }

  private trackPromotion(
    href: string,
    creative: EcommercePromotionSource,
    position: number,
  ) {
    this.store.dispatch(
      gtmTrackActions.gtmEcommerceTrackEvent({
        value: {
          event: EcommerceTrackingEvents.PromotionClick,
          ecommerce: {
            promoClick: {
              promotions: [
                {
                  id: href,
                  name: href,
                  creative,
                  position,
                },
              ],
            },
          },
        },
      }),
    );
  }
}
